import {
  apiClient,
} from "./apiClient";

import type {
  CreateInventoryAdjustmentInput,
  CreateInventoryReservationInput,
  InventoryReservationOperationData,
  InventoryReservationReallocationData,
  InventoryReservationsData,
  InventoryReservationsQuery,
  InventoryTransactionsData,
  InventoryTransactionsQuery,
  ProductStockData,
  ReallocateInventoryReservationInput,
  ReleaseInventoryReservationInput,
} from "../types/products.types";

import type {
  WarehouseStockRecord,
  WarehouseStocksPageData,
  WarehouseStocksQuery,
} from "../types/warehouses.types";


/* =========================================================
 * Helpers
 * ======================================================= */

function setStringParam(
  params: URLSearchParams,

  key: string,

  value:
    string | undefined,
) {
  if (!value?.trim()) {
    return;
  }

  params.set(
    key,
    value.trim(),
  );
}


/* =========================================================
 * Inventory Reservations Query
 * ======================================================= */

function buildInventoryReservationsQuery(
  query:
    InventoryReservationsQuery = {},
): string {
  const params =
    new URLSearchParams();


  setStringParam(
    params,
    "productId",
    query.productId,
  );


  if (query.status) {
    params.set(
      "status",
      query.status,
    );
  }


  setStringParam(
    params,
    "orderId",
    query.orderId,
  );


  setStringParam(
    params,
    "reservedBy",
    query.reservedBy,
  );


  setStringParam(
    params,
    "dateFrom",
    query.dateFrom,
  );


  setStringParam(
    params,
    "dateTo",
    query.dateTo,
  );


  setStringParam(
    params,
    "cursor",
    query.cursor,
  );


  if (
    query.limit !==
    undefined
  ) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }


  const queryString =
    params.toString();


  return queryString
    ? `?${queryString}`
    : "";
}


/* =========================================================
 * Inventory Transactions Query
 * ======================================================= */

function buildInventoryTransactionsQuery(
  query:
    InventoryTransactionsQuery = {},
): string {
  const params =
    new URLSearchParams();


  setStringParam(
    params,
    "productId",
    query.productId,
  );


  setStringParam(
    params,
    "warehouseId",
    query.warehouseId,
  );


  if (query.type) {
    params.set(
      "type",
      query.type,
    );
  }


  if (query.source) {
    params.set(
      "source",
      query.source,
    );
  }


  setStringParam(
    params,
    "orderId",
    query.orderId,
  );


  setStringParam(
    params,
    "reservationId",
    query.reservationId,
  );


  setStringParam(
    params,
    "performedBy",
    query.performedBy,
  );


  setStringParam(
    params,
    "dateFrom",
    query.dateFrom,
  );


  setStringParam(
    params,
    "dateTo",
    query.dateTo,
  );


  setStringParam(
    params,
    "cursor",
    query.cursor,
  );


  if (
    query.limit !==
    undefined
  ) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }


  const queryString =
    params.toString();


  return queryString
    ? `?${queryString}`
    : "";
}


/* =========================================================
 * Warehouse Stock Query
 * ======================================================= */

function buildWarehouseStocksQuery(
  query:
    WarehouseStocksQuery = {},
): string {
  const params =
    new URLSearchParams();


  setStringParam(
    params,
    "warehouseId",
    query.warehouseId,
  );


  setStringParam(
    params,
    "productId",
    query.productId,
  );


  if (
    query.inStock !==
    undefined
  ) {
    params.set(
      "inStock",
      String(
        query.inStock,
      ),
    );
  }


  setStringParam(
    params,
    "cursor",
    query.cursor,
  );


  if (
    query.limit !==
    undefined
  ) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }


  const queryString =
    params.toString();


  return queryString
    ? `?${queryString}`
    : "";
}


/* =========================================================
 * Inventory API
 * ======================================================= */

export const inventoryApi = {

  /* =======================================================
   * Transactions
   * ===================================================== */

  getTransactions(
    query:
      InventoryTransactionsQuery = {},
  ) {
    return apiClient.get<
      InventoryTransactionsData
    >(
      `/inventory/transactions${buildInventoryTransactionsQuery(
        query,
      )}`,
    );
  },


  /* =======================================================
   * Manual Adjustment
   * ===================================================== */

  createAdjustment(
    input:
      CreateInventoryAdjustmentInput,
  ) {
    return apiClient.post<
      ProductStockData
    >(
      "/inventory/adjustments",
      input,
    );
  },


  /* =======================================================
   * Warehouse Stock
   * ===================================================== */

  getWarehouseStocks(
    query:
      WarehouseStocksQuery = {},
  ) {
    return apiClient.get<
      WarehouseStocksPageData
    >(
      `/inventory/warehouses/stock${buildWarehouseStocksQuery(
        query,
      )}`,
    );
  },


  getWarehouseProductStock(
    warehouseId: string,

    productId: string,
  ) {
    return apiClient.get<
      WarehouseStockRecord
    >(
      `/inventory/warehouses/${encodeURIComponent(
        warehouseId,
      )}/products/${encodeURIComponent(
        productId,
      )}/stock`,
    );
  },


  /* =======================================================
   * Reservations
   * ===================================================== */

  getReservations(
    query:
      InventoryReservationsQuery = {},
  ) {
    return apiClient.get<
      InventoryReservationsData
    >(
      `/inventory/reservations${buildInventoryReservationsQuery(
        query,
      )}`,
    );
  },


  createReservation(
    input:
      CreateInventoryReservationInput,
  ) {
    return apiClient.post<
      InventoryReservationOperationData
    >(
      "/inventory/reservations",
      input,
    );
  },


  reallocateReservation(
    reservationId: string,

    input:
      ReallocateInventoryReservationInput,
  ) {
    return apiClient.post<
      InventoryReservationReallocationData
    >(
      `/inventory/reservations/${encodeURIComponent(
        reservationId,
      )}/reallocate`,
      input,
    );
  },


  releaseReservation(
    reservationId: string,

    input:
      ReleaseInventoryReservationInput,
  ) {
    return apiClient.post<
      InventoryReservationOperationData
    >(
      `/inventory/reservations/${encodeURIComponent(
        reservationId,
      )}/release`,
      input,
    );
  },
};